import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { AutomationRun, RunStatus } from '../../../types/models/whatsapp';

interface RunLogDialogProps {
  open: boolean;
  runs: AutomationRun[];
  onClose: () => void;
}

const STATUS_TONE: Record<RunStatus, string> = {
  running: 'text-warning-600',
  waiting: 'text-primary-600',
  completed: 'text-success-600',
  failed: 'text-danger-600',
  handed_off: 'text-wa-muted'
};

const STATUS_LABEL: Record<RunStatus, string> = {
  running: 'Running',
  waiting: 'Waiting',
  completed: 'Completed',
  failed: 'Failed',
  handed_off: 'Handed to agent'
};

const FILTERS: Array<{ label: string; value: RunStatus | 'all' }> = [
  { label: 'All', value: 'all' },
  { label: 'Waiting', value: 'waiting' },
  { label: 'Completed', value: 'completed' },
  { label: 'Failed', value: 'failed' },
  { label: 'Handed off', value: 'handed_off' }
];

const formatTime = (timestamp: number): string =>
  timestamp
    ? new Date(timestamp).toLocaleString([], {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      })
    : '—';

/**
 * Run log — the answer to "why did this contact not get a reply?".
 *
 * Failures carry their error, and a waiting run shows what it is waiting for,
 * which is usually enough to spot a flow that parked on a question nobody
 * answered.
 */
const RunLogDialog: React.FC<RunLogDialogProps> = ({ open, runs, onClose }) => {
  const [filter, setFilter] = useState<RunStatus | 'all'>('all');

  const filtered = useMemo(
    () => (filter === 'all' ? runs : runs.filter((r) => r.status === filter)),
    [runs, filter]
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-wa-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-wa-border px-4 py-3">
          <h2 className="text-sm font-semibold text-wa-text">
            Run log
            <span className="ml-2 text-xs font-normal text-wa-muted">
              {filtered.length} run{filtered.length === 1 ? '' : 's'}
            </span>
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-wa-muted hover:bg-wa-surface2"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex gap-1 overflow-x-auto border-b border-wa-border p-2">
          {FILTERS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setFilter(option.value)}
              className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                filter === option.value
                  ? 'bg-wa-green text-white'
                  : 'bg-wa-surface2 text-wa-muted hover:text-wa-text'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {filtered.length === 0 && (
            <p className="p-8 text-center text-xs text-wa-muted">
              No runs yet. Publish a flow and message the business number to
              test it.
            </p>
          )}

          {filtered.map((run) => (
            <div
              key={run.id}
              className="border-b border-wa-border px-4 py-2.5"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs font-medium text-wa-text">
                    {run.flowName || run.flowId}
                    <span className="ml-1.5 font-normal text-wa-muted">
                      v{run.flowVersion}
                    </span>
                  </span>
                  <span className="block text-[11px] text-wa-muted">
                    Step: <code className="font-mono">{run.currentNodeId}</code>
                    {run.waitingFor?.type === 'reply' && ' · awaiting a reply'}
                    {run.waitingFor?.type === 'delay' &&
                      run.waitingFor.until &&
                      ` · resumes ${formatTime(run.waitingFor.until)}`}
                  </span>
                  {run.error && (
                    <span className="mt-0.5 block text-[11px] text-danger-600">
                      {run.error}
                    </span>
                  )}
                  {Object.keys(run.variables || {}).length > 0 && (
                    <span className="mt-0.5 block truncate font-mono text-[10px] text-wa-muted">
                      {Object.entries(run.variables)
                        .map(([k, v]) => `${k}=${v}`)
                        .join('  ')}
                    </span>
                  )}
                </span>

                <span className="shrink-0 text-right">
                  <span
                    className={`block text-[11px] font-medium ${
                      STATUS_TONE[run.status]
                    }`}
                  >
                    {STATUS_LABEL[run.status]}
                  </span>
                  <span className="block text-[10px] text-wa-muted">
                    {formatTime(run.startedAt)}
                  </span>
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RunLogDialog;
